import React, { useCallback, useEffect, useRef, useState } from 'react';
import { platformService } from '../shared/services/platform.service';
import '../AppGlobals';
import { DEFAULT_API_STATUS, DONE, ERROR, getModalSubheading, LOADING } from '../shared/utils/constants';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Dialog, Flex, Input, Loader, TextArea } from '@fluentui/react-northstar';
import { CheckLicense } from '../shared/components/License/CheckLicense';
import { LabelElement } from '../shared/components/Modal/Modal';
import OfferingSvg from '../../svg/service-offering.svg';
import { EditIconProperty } from '../shared/components/EditIconProperty/EditIconProperty';

interface Props {
  id: number;
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

export const Offering: React.FC<Props> = ({ id, open, onCancel, onSuccess }) => {
  const api = new platformService();
  const { t } = useTranslation();
  const [data, setData] = useState<any>({});

  const [apiStatus, setAPIStatus] = useState<APIStatus>(DEFAULT_API_STATUS as APIStatus);

  const fetchData = useCallback(async () => {
    setAPIStatus(prev => ({ ...prev, status: LOADING, msg: t('service-offering.loading')}));

    try {
      const { data } = await api.getServiceOfferings(`(${id})`);
      setData(data);
    } catch (err: any) {
      setAPIStatus(prev => ({ ...prev, status: ERROR, errCode: err.response.status }));
    } finally {
      setAPIStatus(prev => ({ ...prev, status: DONE }));
    }
  }, [id]);

  const fetchDefault = useCallback(async () => {
    setAPIStatus(prev => ({ ...prev, status: LOADING, msg: t('service-offering.loading')}));

    try {
      const { data: { value } } = await api.getServiceOfferingDefault();
      setData(JSON.parse(value));
    } catch (err: any) {
      setAPIStatus(prev => ({ ...prev, status: ERROR, errCode: err.response.status }));
    } finally {
      setAPIStatus(prev => ({ ...prev, status: DONE }));
    }
  }, []);


  useEffect(() => {
    if (id > 0 && open) 
      fetchData();
    else 
      fetchDefault();
  }, [id, open]);

  const saveData = async () => {
    setAPIStatus(prev => ({ ...prev, status: LOADING, msg: t('service-offering.saving-data') }));

    try {
      if (id <= 0) {
        const count = await api.getServiceOfferings(`/$count`);
        data.Order = count.data;
        await api.createOffering(data);
      } else 
        await api.updateOffering(data);

      onSuccess();
    } catch (err: any) {
      setAPIStatus(prev => ({ ...prev, status: ERROR, errCode: err.response.status }));
    }
  };

  const pageIsValid: () => boolean = () =>  (data?.Name ?? "") !== "";


  const renderFooter = () => (
    <Flex hAlign="end" gap="gap.smaller">
        <Button content={t('common.buttons.cancel')} onClick={onCancel} secondary disabled={apiStatus.status === LOADING} />
        <CheckLicense disable>
          <Button content={t('common.buttons.save')} onClick={saveData} primary disabled={!pageIsValid() || apiStatus.status === LOADING} />
        </CheckLicense>
    </Flex>);

  const renderContent = () => {
    if (apiStatus.status === LOADING) return <Loader label={apiStatus.msg} />;

    return (<div>
      <h6 className='pb-4'>{getModalSubheading(id > 0, t('service-offering.title'))}</h6>
      <Flex column gap="gap.small">
        <LabelElement label={t('service-offering.fields.name')} required>
          <div className="teams-input-med">
            <Input clearable value={data.Name} onChange={(e, t) => { setData(p => ({ ...p, Name: t.value })); }} fluid />
          </div>
        </LabelElement>
        <LabelElement label={t('service-offering.fields.description')}>
          <div className="teams-input-med">
            <TextArea variables={{ height: "4em" }} value={data.Description} onChange={(e, t) => { setData(p => ({ ...p, Description: t.value })); }} fluid />
          </div>
        </LabelElement>
        <Checkbox label={t('service-offering.fields.expanded')} toggle onChange={(e, x) => { setData(p => ({ ...p, Expanded: x.checked })); }} checked={data.Expanded} />
        <LabelElement label={t('service-offering.fields.icon')}>
          <EditIconProperty icon={data.Icon} defaultIcon={<OfferingSvg fill="#6264A7" width={40} height={40} />} onUpload={img => { setData(p => ({ ...p, Icon: img })); }} onRemove={() => { setData(p => ({ ...p, Icon: '' })); }} />
        </LabelElement>
      </Flex>
    </div>);
  };

  return (
    <Dialog open={open} closeOnOutsideClick={false} footer={renderFooter()} header={t('service-offering.title')} content={renderContent()} style={{ maxWidth: '550px' }} />
  );
};
